import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Ear, Eye, Sparkles, ShieldCheck, Lock, Book, HeartHandshake, Trophy, ArrowRight,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { BigButton } from "../design/BigButton";
import { ASSETS } from "../lib/assets";
import "./landing.css";

const FAQ = [
  { q: "What age is With My Little EYE for?",
    a: "Designed for children ages 3 to 8. Parents choose an age band (2-3, 4-5, 6-7, 8-9) and difficulty per child." },
  { q: "Does it work offline?",
    a: "Not yet. The current release needs an internet connection so we can keep content fresh. Offline mode is on our roadmap." },
  { q: "How much does it cost?",
    a: "One-time family unlock — one payment, every world, every level, every child profile on your account. No ads, no subscriptions, no in-child purchases." },
  { q: "Is my child safe?",
    a: "Yes. No third-party ads, no analytics on children, no data sold. Parent-gate protects settings. Details on the Privacy page." },
  { q: "Can I get a refund?",
    a: "Yes — 7 days from date of purchase, full refund, no questions asked. Just email us via the Contact form below. (Note: purchases made through Apple App Store or Google Play follow those stores' refund policies.)" },
  { q: "What's the parent gate?",
    a: "A 4- to 6-digit PIN required to leave gameplay and open settings, purchases, or account controls. Even a curious tap can't bypass it." },
  { q: "What languages are supported?",
    a: "English at launch. Spanish and French are in translation. Vote for more languages via the Contact form!" },
  { q: "Can I export my data?",
    a: "Yes. The Parent → Privacy screen has one-click JSON export and full account deletion." },
];

export function LandingPage() {
  const [contactForm, setContactForm] = useState({ email: "", subject: "", message: "" });
  const [contactBusy, setContactBusy] = useState(false);
  const [contactMsg, setContactMsg] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  function scrollToId(id) {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  async function submitContact(e) {
    e.preventDefault();
    if (!contactForm.email || !contactForm.subject || !contactForm.message) {
      setContactMsg({ ok: false, text: "Please fill in all fields." });
      return;
    }
    setContactBusy(true); setContactMsg(null);
    try {
      await api.post("/support", contactForm);
      setContactMsg({ ok: true, text: "Thanks! We'll get back to you within 2 business days." });
      setContactForm({ email: "", subject: "", message: "" });
    } catch (err) {
      setContactMsg({ ok: false, text: formatApiErrorDetail(err.response?.data?.detail || err.message) });
    } finally { setContactBusy(false); }
  }

  return (
    <div className="landing" data-testid="landing-page">
      <header className="landing__nav" data-testid="landing-nav">
        <div className="landing__brand">
          <img src={ASSETS.logo} alt="With My Little EYE logo" className="landing__logo" />
          <b>With My Little EYE</b>
        </div>
        <nav aria-label="Primary">
          <a href="#how" onClick={(e) => { e.preventDefault(); scrollToId("how"); }}>How it works</a>
          <a href="#features" onClick={(e) => { e.preventDefault(); scrollToId("features"); }}>Features</a>
          <a href="#privacy" onClick={(e) => { e.preventDefault(); scrollToId("privacy"); }}>Privacy</a>
          <a href="#faq" onClick={(e) => { e.preventDefault(); scrollToId("faq"); }}>FAQ</a>
          <Link to="/login" data-testid="landing-nav-signin">Sign in</Link>
          <Link to="/register" className="landing__cta-mini" data-testid="landing-nav-signup">Create account</Link>
        </nav>
      </header>

      <section className="landing__hero" data-testid="landing-hero">
        <motion.div
          className="landing__hero-copy"
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        >
          <h1>With My Little EYE</h1>
          <p className="landing__tagline">Find It. Hear It. Spell It.</p>
          <p className="landing__lead">
            A colorful search-and-find word game for kids. Explore imaginative
            worlds, spot hidden objects, and hear every word out loud — one
            payment, every world, every child on your account, no ads.
          </p>
          <div className="landing__hero-cta">
            <BigButton onClick={() => (window.location.href = "/register")} data-testid="landing-cta-start">
              Start exploring <ArrowRight size={20} />
            </BigButton>
            <BigButton variant="ghost" onClick={() => scrollToId("features")} data-testid="landing-cta-demo">
              See how it works
            </BigButton>
          </div>
          <ul className="landing__badges" aria-label="Highlights">
            <li><ShieldCheck size={18} /> No ads · no tracking</li>
            <li><HeartHandshake size={18} /> Ages 3–8</li>
            <li><Book size={18} /> 5 worlds, 30+ levels</li>
          </ul>
        </motion.div>
        <motion.div
          className="landing__hero-art"
          initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1, duration: 0.5 }}
        >
          <img src={ASSETS.scene} alt="A cheerful toy-store scene filled with a rubber duck, teddy bear, red ball and other hidden objects" />
          <img src={ASSETS.mascot} alt="Ollie the owl mascot" className="landing__hero-mascot" />
        </motion.div>
      </section>

      <section id="features" className="landing__section landing__features" data-testid="landing-features">
        <h2>What every play session feels like</h2>
        <div className="landing__feature-grid">
          <FeatureCard
            icon={<Eye size={28} />}
            title="Tap to find"
            body="Spot the hidden object in a hand-drawn scene. Zoom in, look around, take your time — no timers, no fails."
          />
          <FeatureCard
            icon={<Ear size={28} />}
            title="Hear the word"
            body="Ollie the owl announces every discovery out loud — Spanish and French coming soon."
          />
          <FeatureCard
            icon={<Sparkles size={28} />}
            title="See it spelled"
            body="Watch each letter appear as the word is spoken — playful, satisfying, kid-friendly."
          />
        </div>
      </section>

      <section id="how" className="landing__section landing__how" data-testid="landing-how">
        <h2>How it works</h2>
        <ol className="landing__steps">
          <li>
            <span className="landing__step-num">1</span>
            <h3>Set up a parent account</h3>
            <p>Email-only, protected by a 4-digit PIN. No credit card on the parent&apos;s browser — payment happens on Stripe.</p>
          </li>
          <li>
            <span className="landing__step-num">2</span>
            <h3>Complete the family unlock</h3>
            <p>One payment covers every child profile on your account, forever. No subscriptions, no ads, no in-child purchases.</p>
          </li>
          <li>
            <span className="landing__step-num">3</span>
            <h3>Create a child profile</h3>
            <p>Choose an age band, difficulty, avatar and narration speed. No photos, no birthdays required.</p>
          </li>
          <li>
            <span className="landing__step-num">4</span>
            <h3>Play + watch words come alive</h3>
            <p>Each find earns tokens for the Rewards Room and lands the word in the Word Book — spellings, examples, favorites.</p>
          </li>
        </ol>
      </section>

      <section className="landing__section landing__benefits" data-testid="landing-benefits">
        <h2>Why kids come back</h2>
        <div className="landing__benefit-grid">
          <BenefitCard title="Fun-first gameplay" body="Bright scenes, cheerful characters, and a calm loop that keeps young players engaged without pressure." />
          <BenefitCard title="Every find is a word" body="Ollie says the word, spells it letter by letter, and drops it in your Word Book." />
          <BenefitCard title="No stress, no timers" body="No failure states, no scary countdowns — just gentle progress at your child's pace." />
        </div>
      </section>

      <section id="privacy" className="landing__section landing__privacy" data-testid="landing-privacy">
        <div className="landing__privacy-copy">
          <h2><Lock size={24} /> Privacy-first, always</h2>
          <ul>
            <li><b>No third-party ads.</b> Ever.</li>
            <li><b>No child tracking.</b> We don&apos;t sell, share or profile kids&apos; data.</li>
            <li><b>Parent-gated settings.</b> A PIN protects the app store, purchases and account controls.</li>
            <li><b>Full data export & deletion.</b> One-click JSON export, one-click account removal.</li>
            <li><b>You control everything.</b> Ambient music, motion, high contrast, narration speed — all per profile.</li>
          </ul>
          <Link to="/privacy" className="landing__link" data-testid="landing-privacy-link">Read the privacy policy →</Link>
        </div>
        <div className="landing__privacy-art">
          <img src={ASSETS.mascot} alt="Ollie the owl guarding a small shield" />
        </div>
      </section>

      <section id="screenshots" className="landing__section landing__gallery" data-testid="landing-gallery">
        <h2>A peek inside the app</h2>
        <div className="landing__gallery-grid">
          <img src={ASSETS.scene} alt="Toy Store gameplay: the child is finding a rubber duck" />
          <div className="landing__gallery-card">
            <Trophy size={28} />
            <h3>Word Book</h3>
            <p>Every discovered word lives here with pronunciation, spelling and world tags.</p>
          </div>
          <div className="landing__gallery-card">
            <Book size={28} />
            <h3>Parent dashboard</h3>
            <p>Progress at a glance — weekly activity, practice words, session length. No pressure.</p>
          </div>
          <div className="landing__gallery-card">
            <Sparkles size={28} />
            <h3>Rewards Room</h3>
            <p>Stickers, avatar accessories, room decor — earned by playing after the family unlock.</p>
          </div>
        </div>
      </section>

      <section id="faq" className="landing__section landing__faq" data-testid="landing-faq">
        <h2>Questions parents ask</h2>
        <div className="landing__faq-list">
          {FAQ.map((item, i) => (
            <details
              key={i}
              open={openFaq === i}
              onToggle={(e) => setOpenFaq(e.currentTarget.open ? i : null)}
              data-testid={`landing-faq-${i}`}
            >
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section id="contact" className="landing__section landing__contact" data-testid="landing-contact">
        <div className="landing__contact-copy">
          <h2>Say hi</h2>
          <p>Want a language, a bug report, a feature? We read every message.</p>
        </div>
        <form className="landing__contact-form" onSubmit={submitContact}>
          <label>Your email
            <input
              type="email" required value={contactForm.email}
              onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
              data-testid="landing-contact-email"
            />
          </label>
          <label>Subject
            <input
              required value={contactForm.subject}
              onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
              data-testid="landing-contact-subject"
            />
          </label>
          <label>Message
            <textarea
              required rows={4} value={contactForm.message}
              onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
              data-testid="landing-contact-message"
            />
          </label>
          <BigButton type="submit" disabled={contactBusy} data-testid="landing-contact-submit">
            {contactBusy ? "Sending…" : "Send message"}
          </BigButton>
          {contactMsg && (
            <div className={contactMsg.ok ? "landing__contact-ok" : "landing__contact-err"} data-testid="landing-contact-status">
              {contactMsg.text}
            </div>
          )}
        </form>
      </section>

      <section className="landing__section landing__final-cta" data-testid="landing-final-cta">
        <h2>Ready to explore?</h2>
        <p>Set up your family account in under two minutes, complete the one-time unlock, and start playing.</p>
        <div className="landing__final-cta-buttons">
          <BigButton onClick={() => (window.location.href = "/register")} data-testid="landing-cta-register">Create account</BigButton>
          <BigButton variant="ghost" onClick={() => (window.location.href = "/login")} data-testid="landing-cta-signin">Sign in</BigButton>
        </div>
      </section>

      <footer className="landing__footer" data-testid="landing-footer">
        <div>
          <b>With My Little EYE</b>
          <p style={{ fontSize: "0.72rem", color: "var(--wmleye-ink-soft)", marginTop: 4 }}>
            Trademark and IP clearance for the name are pending — see LAUNCH_CHECKLIST.md.
          </p>
        </div>
        <nav aria-label="Footer">
          <Link to="/legal/privacy" data-testid="footer-privacy-link">Privacy Policy</Link>
          <Link to="/legal/terms" data-testid="footer-terms-link">Terms of Service</Link>
          <Link to="/privacy">Privacy request</Link>
          <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToId("contact"); }}>Contact</a>
          <span>© 2026 With My Little EYE</span>
        </nav>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, body }) {
  return (
    <motion.div whileHover={{ y: -3 }} className="landing__feature-card">
      <span className="landing__feature-icon">{icon}</span>
      <h3>{title}</h3>
      <p>{body}</p>
    </motion.div>
  );
}

function BenefitCard({ title, body }) {
  return (
    <div className="landing__benefit-card">
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  );
}
